import {useMemo, useState, type ReactNode} from 'react';
import {
  groupByCategory,
  type NotificationChannel,
  type NotificationEventDef,
  type UserNotificationType,
} from '@common/types/notifications';
import {Input} from '@common-web/components/ui/input';
import {Switch} from '@common-web/components/ui/switch';
import {Label} from '@common-web/components/ui/label';
import {Badge} from '@common-web/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@common-web/components/ui/collapsible';
import {Bell, ChevronDown, ChevronRight, Lock, Mail, Search, Loader2} from 'lucide-react';
import {cn} from '@common-web/lib/utils';

/** `*Mixed` marks a channel whose underlying logins disagree (shown off, labelled "Mixed"). */
export type ChannelState = {
  email: boolean;
  inApp: boolean;
  emailMixed?: boolean;
  inAppMixed?: boolean;
};

function MixedLabel() {
  return <span className='text-xs text-muted-foreground'>Mixed</span>;
}

const PRIORITY_VARIANT: Record<NotificationEventDef['priority'], string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-amber-100 text-amber-700',
  medium: 'bg-blue-100 text-blue-700',
  info: 'bg-gray-100 text-gray-600',
};

export function NotificationPreferenceList({
  events,
  getChannelState,
  onToggle,
  updatingType,
  readOnly = false,
  emptyState,
}: {
  events: NotificationEventDef[];
  getChannelState: (type: UserNotificationType) => ChannelState;
  onToggle: (type: UserNotificationType, channel: NotificationChannel, value: boolean) => void;
  updatingType?: UserNotificationType | null;
  readOnly?: boolean;
  emptyState?: ReactNode;
}) {
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const grouped = useMemo(() => {
    const q = search.toLowerCase().trim();
    const filtered = q
      ? events.filter(
          (e) => e.label.toLowerCase().includes(q) || e.description.toLowerCase().includes(q),
        )
      : events;
    return groupByCategory(filtered);
  }, [events, search]);

  if (events.length === 0) {
    return (
      <div className='text-sm text-muted-foreground'>
        {emptyState ?? 'No notifications to configure.'}
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <div className='relative'>
        <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder='Search notifications...'
          className='pl-9'
        />
      </div>

      {/* Only reachable via filtering: with a blank search, events.length > 0
          guarantees at least one group. */}
      {grouped.length === 0 && (
        <div className='text-sm text-muted-foreground'>No notifications match your search.</div>
      )}

      {grouped.map(({category, label, events: catEvents}) => {
        const isCollapsed = collapsed[category] ?? false;
        return (
          <Collapsible
            key={category}
            open={!isCollapsed}
            onOpenChange={(open) => setCollapsed((p) => ({...p, [category]: !open}))}
            className='rounded-lg border'
          >
            <CollapsibleTrigger className='flex w-full items-center gap-2 px-4 py-3 text-left'>
              {isCollapsed ? (
                <ChevronRight className='h-4 w-4 text-muted-foreground' />
              ) : (
                <ChevronDown className='h-4 w-4 text-muted-foreground' />
              )}
              <span className='flex-1 text-sm font-semibold'>{label}</span>
              <span className='text-xs text-muted-foreground'>{catEvents.length}</span>
            </CollapsibleTrigger>
            <CollapsibleContent className='divide-y border-t'>
              {catEvents.map((event) => {
                const state = getChannelState(event.key);
                const isUpdating = updatingType === event.key;
                return (
                  <div key={event.key} className='flex items-start gap-4 px-4 py-3'>
                    <div className='flex-1 space-y-1'>
                      <div className='flex flex-wrap items-center gap-2'>
                        <Label className='text-sm font-medium'>{event.label}</Label>
                        <Badge
                          variant='outline'
                          className={cn('text-[10px] uppercase', PRIORITY_VARIANT[event.priority])}
                        >
                          {event.priority}
                        </Badge>
                        {!event.mutable && (
                          <span className='inline-flex items-center gap-1 text-xs text-muted-foreground'>
                            <Lock className='h-3 w-3' /> Always on
                          </span>
                        )}
                      </div>
                      <p className='text-sm text-muted-foreground'>{event.description}</p>
                    </div>
                    <div className='flex shrink-0 items-center gap-4'>
                      {isUpdating && (
                        <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
                      )}
                      {event.channels.includes('inApp') && (
                        <div className='flex items-center gap-2'>
                          <Bell className='h-4 w-4 text-muted-foreground' />
                          {state.inAppMixed && <MixedLabel />}
                          <Switch
                            checked={state.inApp}
                            disabled={readOnly || !event.mutable || isUpdating}
                            onCheckedChange={(v) => onToggle(event.key, 'inApp', v)}
                            aria-label={`${event.label} in-app${state.inAppMixed ? ' (mixed)' : ''}`}
                          />
                        </div>
                      )}
                      {event.channels.includes('email') && (
                        <div className='flex items-center gap-2'>
                          <Mail className='h-4 w-4 text-muted-foreground' />
                          {state.emailMixed && <MixedLabel />}
                          <Switch
                            checked={state.email}
                            disabled={readOnly || !event.mutable || isUpdating}
                            onCheckedChange={(v) => onToggle(event.key, 'email', v)}
                            aria-label={`${event.label} email${state.emailMixed ? ' (mixed)' : ''}`}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </div>
  );
}
